/**
 * Settings persistence. Reads on boot, writes on change.
 */

import { getSettings, putSettings } from './lib/storage.js';
import { getState, setSettings } from './state.js';
import {
  applyThemePreference,
  resolveThemePreferenceForLoad,
  isValidThemePreference
} from './lib/theme.js';

const DEFAULTS = {
  matchStrategy:     'normalized',
  matchThreshold:    0.85,
  applyMode:         'auto',
  dateFormat:        'YYYY-MM-DD',
  csvBom:            false,
  model:             'claude-sonnet-4-6',
  csvFilenamePrefix: 'invoices',
  backupRuleGenerations: 5,
  themePreference:   'system'
};

export async function loadAndApplySettings() {
  let saved;
  try { saved = await getSettings(); }
  catch { saved = null; }

  const merged = { ...DEFAULTS, ...(saved || {}) };
  // The model isn't user-configurable for now — always Sonnet 4.6.
  merged.model = DEFAULTS.model;

  merged.themePreference = resolveThemePreferenceForLoad(
    saved || null,
    merged.themePreference
  );
  if (!isValidThemePreference(merged.themePreference)) {
    merged.themePreference = DEFAULTS.themePreference;
  }

  setSettings(merged);
  // Keep localStorage + document theme in lockstep with merged preference (fixes IDB↔LS drift).
  applyThemePreference(merged.themePreference);

  // Repair IndexedDB if older rows never stored themePreference — keep localStorage & state in sync.
  if (
    saved &&
    merged.themePreference !== saved.themePreference &&
    isValidThemePreference(merged.themePreference)
  ) {
    try {
      await putSettings(merged);
    } catch {
      /* ignore — non-fatal */
    }
  }

  return merged;
}

export async function saveSettings(patch) {
  const current = getState().settings;
  const next = { ...current, ...patch, model: DEFAULTS.model };
  if (patch.themePreference !== undefined) {
    next.themePreference = isValidThemePreference(patch.themePreference)
      ? patch.themePreference
      : DEFAULTS.themePreference;
  } else if (!isValidThemePreference(next.themePreference)) {
    next.themePreference = DEFAULTS.themePreference;
  }
  setSettings(next);
  // Mirror theme to localStorage before IndexedDB so a failed/stale IDB row cannot override on reload.
  applyThemePreference(next.themePreference);
  await putSettings(next);
  return next;
}
